/**
 * Availability cell classification — one state per Resource per month.
 *
 * A cell compares Booked Hours to Available Hours. The percentage is rounded to
 * one decimal BEFORE it is compared to the cutoffs, so the number a planner sees
 * and the state it lands in always agree (74.95% shows as 75.0% and is Some Room).
 *
 * Precedence: No Data (no available hours to compare against), then Unplanned
 * (past the plan horizon with no Guidance — a zero there means "not planned
 * yet", not "free"), then the percentage bands from the top down.
 *
 * Cutoffs are passed in so the settings page can tune them; the defaults are
 * {@link DEFAULT_AVAILABILITY_CUTOFFS}.
 *
 * 🔒 FIREWALL: these are HOURS, and hours are Utilization. "Over-utilized" is
 * never a synonym for "over capacity" (points). No Fit Score, Capacity,
 * Intensity, or points imports.
 */

export type CellState =
  | "overUtilized"
  | "stretched"
  | "fullyBooked"
  | "someRoom"
  | "available"
  | "unplanned"
  | "noData";

/** The two states a planner can book more work into. */
export type FreeCellState = "someRoom" | "available";

export interface AvailabilityCutoffs {
  /** At or above this booked % a cell is Some Room (below it: Available). */
  someRoomFloorPct: number;
  /** At or above this booked % a cell is Fully Booked. */
  fullyBookedFloorPct: number;
  /** At or above this booked % a cell is Stretched. */
  stretchedThresholdPct: number;
  /** At or above this booked % a cell is Over-utilized. */
  overThresholdPct: number;
}

export const DEFAULT_AVAILABILITY_CUTOFFS: AvailabilityCutoffs = {
  someRoomFloorPct: 75,
  fullyBookedFloorPct: 90,
  stretchedThresholdPct: 105,
  overThresholdPct: 115,
};

export interface CellInput {
  /** Available Hours for the month, or null when unknown. */
  availableHours: number | null;
  /** Booked Hours (Guidance, or the blended actuals + forecast for the current month). */
  bookedHours: number;
  /** True when the month is after the plan horizon. */
  pastPlanHorizon: boolean;
  /** True when the Resource has any Guidance in the month. */
  hasGuidance: boolean;
}

export interface CellResult {
  state: CellState;
  /**
   * Available minus Booked, rounded to one decimal. POSITIVE means free hours,
   * NEGATIVE means hours over. 0 for a No Data cell, which has nothing to net.
   */
  netHours: number;
  /** Booked ÷ Available × 100, one decimal. Null for No Data and Unplanned cells. */
  pct: number | null;
}

/** Round to one decimal place, so a stored value and a displayed one agree. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function classifyCell(input: CellInput, cutoffs: AvailabilityCutoffs): CellResult {
  const { availableHours, bookedHours } = input;

  if (availableHours === null || availableHours <= 0) {
    return { state: "noData", netHours: 0, pct: null };
  }

  const netHours = round1(availableHours - bookedHours);

  if (input.pastPlanHorizon && !input.hasGuidance) {
    return { state: "unplanned", netHours, pct: null };
  }

  const pct = round1((bookedHours / availableHours) * 100);

  let state: CellState = "available";
  if (pct >= cutoffs.overThresholdPct) state = "overUtilized";
  else if (pct >= cutoffs.stretchedThresholdPct) state = "stretched";
  else if (pct >= cutoffs.fullyBookedFloorPct) state = "fullyBooked";
  else if (pct >= cutoffs.someRoomFloorPct) state = "someRoom";

  return { state, netHours, pct };
}

/** True for a state a planner can book more work into. */
export function isFreeState(state: CellState): state is FreeCellState {
  return state === "someRoom" || state === "available";
}

/** True for a state above the stretched threshold. */
export function isOverState(state: CellState): boolean {
  return state === "overUtilized" || state === "stretched";
}

/**
 * The number printed in a cell: `"+N"` hours over for Over-utilized and
 * Stretched, `"N"` free hours for Some Room and Available, and null for Fully
 * Booked, Unplanned, and No Data (the UI renders "–" for No Data).
 */
export function displayValue(result: CellResult): string | null {
  const hours = Math.round(Math.abs(result.netHours));
  if (isOverState(result.state)) return `+${hours}`;
  if (isFreeState(result.state)) return String(hours);
  return null;
}
